import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, Plus, Calendar as CalIcon, Clock, MapPin, User as UserIcon,
} from "lucide-react";

const STATUS_COLORS = {
  agendado: "bg-blue-100 text-blue-800 border-blue-300",
  en_progreso: "bg-amber-100 text-amber-800 border-amber-300",
  completado: "bg-emerald-100 text-emerald-800 border-emerald-300",
  cancelado: "bg-slate-100 text-slate-500 border-slate-300",
};
const STATUS_LABELS = { agendado: "Agendado", en_progreso: "En progreso", completado: "Completado", cancelado: "Cancelado" };

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function startOfWeek(d) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // Monday=0
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
}
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }

export default function CalendarPage() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [view, setView] = useState(params.get("view") || "month");
  const [cursor, setCursor] = useState(new Date());
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const range = useMemo(() => {
    if (view === "month") {
      const start = startOfWeek(startOfMonth(cursor));
      const end = addDays(startOfWeek(endOfMonth(cursor)), 41);
      return { start, end };
    } else if (view === "week") {
      const s = startOfWeek(cursor);
      return { start: s, end: addDays(s, 6) };
    } else {
      const s = new Date(cursor); s.setHours(0, 0, 0, 0);
      return { start: s, end: addDays(s, 0) };
    }
  }, [view, cursor]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/jobs", {
        params: {
          date_from: range.start.toISOString(),
          date_to: new Date(range.end.getTime() + 86400000 - 1).toISOString(),
        },
      });
      setJobs(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [view, cursor.getTime()]);

  const shift = (n) => {
    const c = new Date(cursor);
    if (view === "month") c.setMonth(c.getMonth() + n);
    else if (view === "week") c.setDate(c.getDate() + 7 * n);
    else c.setDate(c.getDate() + n);
    setCursor(c);
  };

  const today = () => setCursor(new Date());

  const changeView = (v) => {
    setView(v);
    const p = new URLSearchParams(params);
    p.set("view", v);
    setParams(p);
  };

  const monthName = cursor.toLocaleDateString("es-MX", { month: "long", year: "numeric" });

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto space-y-6" data-testid="calendar-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] font-black tracking-[0.24em] uppercase text-[#005A91]">Módulo</div>
          <h1 className="text-3xl lg:text-4xl font-black text-[#0A1118] tracking-tight">Agenda de trabajos</h1>
          <p className="text-slate-500 text-sm mt-1 capitalize">{monthName}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="bg-slate-100 rounded-md p-1 flex gap-1">
            {["month", "week", "day"].map((v) => (
              <button
                key={v}
                data-testid={`view-${v}-btn`}
                onClick={() => changeView(v)}
                className={`text-xs font-bold px-3 py-1.5 rounded transition-colors duration-150 uppercase tracking-wider ${
                  view === v ? "bg-white text-[#005A91] shadow-sm" : "text-slate-600"
                }`}
              >
                {v === "month" ? "Mes" : v === "week" ? "Semana" : "Día"}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" onClick={() => shift(-1)} data-testid="cal-prev" className="h-10 w-10 border-[#005A91]/20">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" onClick={today} data-testid="cal-today" className="h-10 border-[#005A91]/20 font-medium">
              Hoy
            </Button>
            <Button variant="outline" size="icon" onClick={() => shift(1)} data-testid="cal-next" className="h-10 w-10 border-[#005A91]/20">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <Button onClick={() => nav("/jobs/new")} data-testid="new-job-btn" className="bg-[#2E75B5] hover:bg-[#3a88cd] h-10 px-4 font-bold text-white">
            <Plus className="h-4 w-4 mr-1.5" /> Nuevo trabajo
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="pe-card p-10 text-center text-slate-500" data-testid="cal-loading">Cargando…</div>
      ) : view === "month" ? (
        <MonthGrid start={range.start} cursor={cursor} jobs={jobs} onJob={(j) => nav(`/jobs/${j.id}`)} />
      ) : (
        <WeekOrDayList range={range} jobs={jobs} onJob={(j) => nav(`/jobs/${j.id}`)} />
      )}
    </div>
  );
}

function MonthGrid({ start, cursor, jobs, onJob }) {
  const days = Array.from({ length: 42 }, (_, i) => addDays(start, i));
  const current = cursor.getMonth();
  const today = new Date(); today.setHours(0, 0, 0, 0);

  const byDay = {};
  jobs.forEach((j) => {
    const d = new Date(j.scheduled_at);
    const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    (byDay[key] ||= []).push(j);
  });

  return (
    <div className="pe-card overflow-hidden">
      <div className="grid grid-cols-7 bg-[#005A91] text-white text-[10px] font-black uppercase tracking-wider">
        {["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"].map((d) => (
          <div key={d} className="py-2.5 text-center">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((day) => {
          const key = `${day.getFullYear()}-${day.getMonth()}-${day.getDate()}`;
          const list = byDay[key] || [];
          const isCurrent = day.getMonth() === current;
          const isToday = day.getTime() === today.getTime();
          return (
            <div
              key={key}
              data-testid={`day-cell-${key}`}
              className={`min-h-[110px] border-b border-r border-slate-100 p-1.5 ${isCurrent ? "bg-white" : "bg-slate-50/60"}`}
            >
              <div className={`text-xs font-bold mb-1 ${isToday ? "bg-[#4ECCC8] text-[#005A91] rounded-full w-6 h-6 flex items-center justify-center" : isCurrent ? "text-[#0A1118]" : "text-slate-400"}`}>
                {day.getDate()}
              </div>
              <div className="space-y-1">
                {list.slice(0, 3).map((j) => (
                  <button
                    key={j.id}
                    data-testid={`day-job-${j.id}`}
                    onClick={() => onJob(j)}
                    className={`w-full text-left text-[10px] font-bold uppercase px-1.5 py-1 rounded border truncate transition-colors duration-150 hover:opacity-80 ${STATUS_COLORS[j.status]}`}
                    title={j.title}
                  >
                    <span className="font-numbers">{new Date(j.scheduled_at).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}</span> · {j.title}
                  </button>
                ))}
                {list.length > 3 && (
                  <div className="text-[10px] text-slate-500 font-bold px-1.5">+{list.length - 3} más</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WeekOrDayList({ range, jobs, onJob }) {
  const days = [];
  let cur = new Date(range.start);
  while (cur <= range.end) { days.push(new Date(cur)); cur = addDays(cur, 1); }
  const byDay = {};
  jobs.forEach((j) => {
    const d = new Date(j.scheduled_at);
    const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    (byDay[key] ||= []).push(j);
  });
  return (
    <div className="space-y-4">
      {days.map((d) => {
        const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
        const list = (byDay[key] || []).sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
        return (
          <div key={key} className="pe-card p-5" data-testid={`day-list-${key}`}>
            <div className="flex items-baseline gap-3 mb-3">
              <div className="text-[11px] font-black tracking-[0.24em] uppercase text-[#005A91]">
                {d.toLocaleDateString("es-MX", { weekday: "long" })}
              </div>
              <div className="text-2xl font-black text-[#0A1118] font-numbers">
                {d.getDate()} <span className="text-sm text-slate-500 font-medium capitalize">{d.toLocaleDateString("es-MX", { month: "long" })}</span>
              </div>
            </div>
            {list.length === 0 ? (
              <div className="text-sm text-slate-400 italic">Sin trabajos</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {list.map((j) => (
                  <button
                    key={j.id}
                    data-testid={`week-job-${j.id}`}
                    onClick={() => onJob(j)}
                    className="w-full py-3 flex items-center gap-4 text-left hover:bg-slate-50 rounded-md px-2 transition-colors duration-150"
                  >
                    <div className="font-numbers font-black text-[#005A91] w-16 text-lg">
                      {new Date(j.scheduled_at).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-[#0A1118] truncate">{j.title}</div>
                      <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                        <UserIcon className="h-3 w-3" /> {j.client?.name}
                        {j.address && (<><MapPin className="h-3 w-3 ml-1" /> <span className="truncate">{j.address}</span></>)}
                      </div>
                    </div>
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded border ${STATUS_COLORS[j.status]}`}>
                      {STATUS_LABELS[j.status]}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
